/**
 * Castellan Security — SyncEngine
 *
 * After every successful pull, dispatches 'castellan:pull-complete' on window.
 * Desktop and mobile stores listen for this event and re-hydrate from localStorage.
 * This bypasses the component lifecycle entirely — no race conditions.
 */
import { create } from 'zustand'
import { workerGetV1, workerPostV1 } from '../api/worker'
import { mergePostingToPersonnel } from './postingBridge'

export type SyncStatus = 'idle' | 'pulling' | 'pushing' | 'ok' | 'error'

const LS_KEYS = [
  'fom_personnel', 'fom_deskOfficer', 'fom_supervisors', 'fom_secondaryTasks',
  'fom_securityKeys', 'fom_flashlights', 'fom_supervisorRadios',
  'fom_officerRadios', 'fom_otherRadios', 'fom_recorders', 'fom_oos_state',
  'fom_currentDate', 'fom_security_summary', 'fom_daily_posting', 'fom_notes',
  'fom_otherKeys', 'fom_accessBadges', 'fom_satPhones',
  'fom_roster_badge_radio', // durable roster badge/radio assignments
  'fom_roster',            // full roster with badge/radio — syncs from D1 roster table
  'fom_daily_posting_next', // next shift draft — must persist across devices
  'fom_equipment_assignments', // per-officer equipment — syncs desktop edits to mobile
]

function clearSession() {
  localStorage.removeItem('fom_auth_session')
  localStorage.removeItem('fom_desk_confirmed')
}

function localToday(): string {
  try {
    const s = new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric', month: '2-digit', day: '2-digit', hour12: false,
    })
    const [date] = s.split(',')
    const [m, d, y] = date.trim().split('/')
    return `${y}-${m}-${d}`
  } catch {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  }
}

function currentShiftName(): string {
  try {
    const s = new Date().toLocaleString('en-US', {
      timeZone: 'America/New_York', hour: '2-digit', hour12: false,
    })
    const h = parseInt(s.split(':')[0] ?? s, 10)
    if (h >= 7 && h < 15) return 'Morning'
    if (h >= 15 && h < 23) return 'Evening'
    return 'Night'
  } catch { return 'Morning' }
}

function buildPayload(): Record<string, unknown> {
  const payload: Record<string, unknown> = {}
  for (const key of LS_KEYS) {
    const raw = localStorage.getItem(key)
    if (raw !== null) payload[key] = raw
  }
  payload._ts = Date.now()
  // Version-stamp for lightweight 20-second polling
  if (typeof window !== 'undefined') {
    if (!(window as any)._fomSessionId) {
      (window as any)._fomSessionId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2)
    }
    payload._pushClientId = (window as any)._fomSessionId
    payload._pushTs = new Date().toISOString()
  }
  return payload
}

function hasRealData(): boolean {
  try {
    const pers = JSON.parse(localStorage.getItem('fom_personnel') ?? '[]') as { name?: string }[]
    if (pers.some(p => p.name)) return true
  } catch { /* silent */ }
  try {
    const roster = JSON.parse(localStorage.getItem('fom_roster') ?? '{}') as Record<string, unknown[]>
    if (Object.values(roster).flat().length > 0) return true
  } catch { /* silent */ }
  try {
    const posting = JSON.parse(localStorage.getItem('fom_daily_posting') ?? '{}')
    if (posting.supervisors?.length > 0 || posting.officers?.length > 0) return true
  } catch { /* silent */ }
  return false
}

interface SyncState {
  status: SyncStatus
  lastPull: number
  lastPush: number
  error: string
  dirtyAt: number
  pullCount: number
  markDirty: () => void
  pull: () => Promise<void>
  push: () => Promise<void>
  forcePull: () => Promise<void>
  forcePush: () => Promise<void>
}

let _lastHash = ''
let _pulling = false
let _pushing = false
let _pushTimer: ReturnType<typeof setTimeout> | null = null

// ── Per-key last-write-wins tracking ─────────────────────────────────────────
// When save() is called for a key, its write timestamp is stored here.
// During a pull, any key whose local write timestamp is NEWER than the last
// successful push timestamp is preserved — D1 doesn't have our change yet.
// Cleared after a successful push so future pulls can bring in cloud updates.
// PERSISTED to localStorage so an auto-refresh (deploy reload) can't drop
// dirty protection for edits that haven't reached the cloud yet.
const _localKeyTs: Record<string, number> = (() => {
  try { return JSON.parse(localStorage.getItem('_castellan_dirty_ts') || '{}') } catch { return {} }
})()

/** Call this whenever a key is written locally (from desktop.ts save()). */
export function markKeyDirty(key: string): void {
  _localKeyTs[key] = Date.now()
  try { localStorage.setItem('_castellan_dirty_ts', JSON.stringify(_localKeyTs)) } catch { /* silent */ }
}

// Equipment/config keys: a brand-new shift row in D1 returns '[]'/'{}' for
// these until someone pushes real data. Blanking local state from an empty
// cloud value would reset equipment lists (and their OOS flags) to defaults
// at every shift boundary — see empty-cloud guards in pull().
const EQUIP_KEYS = new Set([
  'fom_securityKeys', 'fom_flashlights', 'fom_supervisorRadios',
  'fom_officerRadios', 'fom_otherRadios', 'fom_recorders',
  'fom_otherKeys', 'fom_accessBadges', 'fom_satPhones',
  'fom_equipment_assignments',
])

export const useSyncStore = create<SyncState>((set, get) => ({
  status: 'idle',
  lastPull: 0,
  lastPush: 0,
  error: '',
  dirtyAt: 0,
  pullCount: 0,

  markDirty() {
    set({ dirtyAt: Date.now() })
    // Auto-push 2s after last edit so changes persist before a refresh/pull can revert them
    if (_pushTimer) clearTimeout(_pushTimer)
    _pushTimer = setTimeout(() => {
      get().push()
    }, 2000)
  },

  async pull() {
    if (_pulling) return
    const { dirtyAt, lastPush } = get()
    if (dirtyAt && Date.now() - dirtyAt < 10_000) return
    // Skip pull if we just pushed — give D1/KV time to propagate
    if (lastPush && Date.now() - lastPush < 5_000) return

    // ── Auto hard-refresh on new deployment ────────────────────────────────
    // /version.json is written by deploy.yml with the build timestamp.
    // If it changed since this session started, the browser/WebView gets
    // a hard reload so every device always runs the latest deployed code.
    try {
      const ver = await fetch('/version.json?t=' + Date.now()).then(r => r.json()) as { build?: string }
      const stored = sessionStorage.getItem('_castellan_build')
      if (stored && ver.build && stored !== String(ver.build)) {
        sessionStorage.setItem('_castellan_build', String(ver.build))
        window.location.reload()
        return
      }
      if (!stored && ver.build) sessionStorage.setItem('_castellan_build', String(ver.build))
    } catch { /* version.json may not exist yet — silent until first deploy writes it */ }
    // ─────────────────────────────────────────────────────────────────────────

    _pulling = true
    set({ status: 'pulling' })

    try {
      const cloud = await workerGetV1<Record<string, unknown>>('/api/v1/sync/pull')

      if (cloud._clearSignal) {
        const lastSeen = localStorage.getItem('fom_last_clear_signal') ?? ''
        if (cloud._clearSignal !== lastSeen) {
          localStorage.setItem('fom_last_clear_signal', cloud._clearSignal as string)
          const shiftKeys = [
            'fom_personnel','fom_deskOfficer','fom_supervisors','fom_secondaryTasks',
            'fom_daily_posting','fom_security_summary','fom_notes',
          ]
          shiftKeys.forEach(k => localStorage.removeItem(k))
          // Stamp BOTH reset guards so the sync loop and mergePostingToPersonnel
          // both skip restoring stale shift data for the next 5 minutes
          const clearNow = String(Date.now())
          sessionStorage.setItem('fom_shift_reset_ts', clearNow)
          localStorage.setItem('fom_shift_reset_ts_persist', clearNow)
          // Also stamp shift-key guard so restores are blocked for this whole shift
          try {
            const etStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })
            const [hStr, mStr] = etStr.split(':')
            const totalMins = parseInt(hStr) * 60 + parseInt(mStr)
            const shift = totalMins >= 6*60+55 && totalMins < 14*60+55 ? 'Morning'
                        : totalMins >= 14*60+55 && totalMins < 22*60+55 ? 'Evening' : 'Night'
            const dateStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })
            localStorage.setItem('fom_reset_shift_key', `${dateStr}-${shift}`)
          } catch { /* silent */ }
        }
      }

      // Snapshot local roster BEFORE the KV sync loop overwrites fom_roster
      // This preserves user-set badge/radio values against stale KV data
      const prepullRosterSnapshot: Record<string, any> = (() => {
        try { return JSON.parse(localStorage.getItem('fom_roster') ?? '{}') } catch { return {} }
      })()

      // Keys that must NOT be restored from cloud during a reset window
      // — these are wiped by resetShift and must stay clear until the NEXT shift starts.
      // NOTE: fom_daily_posting is intentionally NOT protected — the supervisor fills
      // it in on mobile for the new shift and we always want that to come through.
      // NOTE: fom_secondaryTasks and fom_notes are NOT protected — they contain NEW
      // shift assignments added by the supervisor after the reset. Protecting them
      // blocks task updates from ever reaching mobile for the rest of the shift.
      const RESET_PROTECTED_KEYS = new Set([
        'fom_personnel', 'fom_deskOfficer', 'fom_supervisors',
        'fom_security_summary',
      ])

      // Shift-based reset guard — persists for the entire shift, not just 5 minutes.
      // When resetShift() runs it stamps fom_reset_shift_key with the current shift.
      // The guard blocks cloud restores until the shift changes (key no longer matches).
      const inResetWindow = (() => {
        const resetKey = localStorage.getItem('fom_reset_shift_key')
        if (!resetKey) return false
        // CRITICAL: never block if local personnel is empty.
        // Fresh APK install, update, or cleared storage must always pull from cloud.
        try {
          const lp = JSON.parse(localStorage.getItem('fom_personnel') ?? '[]') as { name?: string }[]
          if (!Array.isArray(lp) || lp.filter(p => p.name).length === 0) return false
        } catch { return false }
        // Compute current shift key (same logic as desktop.ts getShiftKey)
        try {
          const etStr = new Date().toLocaleString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false })
          const [hStr, mStr] = etStr.split(':')
          const totalMins = parseInt(hStr) * 60 + parseInt(mStr)
          const shift = totalMins >= 6*60+55 && totalMins < 14*60+55 ? 'Morning'
                      : totalMins >= 14*60+55 && totalMins < 22*60+55 ? 'Evening' : 'Night'
          const dateStr = new Date().toLocaleDateString('en-US', { timeZone: 'America/New_York' })
          const currentKey = `${dateStr}-${shift}`
          return resetKey === currentKey
        } catch {
          return false
        }
      })()

      for (const key of LS_KEYS) {
        // During reset window, never restore shift-personnel data from cloud
        if (inResetWindow && RESET_PROTECTED_KEYS.has(key)) continue
        const cloudVal = cloud[key]
        if (cloudVal === undefined || cloudVal === null) continue
        const localVal = localStorage.getItem(key)
        if (localVal === cloudVal) continue

        // CRITICAL: fom_roster_badge_radio must NEVER be overwritten with
        // empty/smaller data. Only merge — never reduce.
        if (key === 'fom_roster_badge_radio') {
          try {
            const cloudObj = typeof cloudVal === 'string' ? JSON.parse(cloudVal) : cloudVal as Record<string, any>
            const localObj = localVal ? JSON.parse(localVal) : {}
            const cloudEntries = Object.keys(cloudObj).length
            const localEntries = Object.keys(localObj).length
            // If cloud has more entries, merge cloud into local (cloud wins for new entries)
            if (cloudEntries >= localEntries) {
              // Merge: local values win for existing keys, cloud wins for new keys
              const merged = { ...cloudObj, ...localObj }
              localStorage.setItem(key, JSON.stringify(merged))
            }
            // If local has more entries than cloud, keep local — never downgrade
          } catch { /* silent */ }
          continue
        }

        // fom_oos_state: LAST-WRITE-WINS — the newest writer (local or cloud) wins.
        // If this device changed OOS since its last successful push, keep the local
        // copy (the change hasn't reached D1 yet). Otherwise take cloud VERBATIM so
        // removals made on other devices propagate here. (The old union merge made
        // it impossible to ever take an item OUT of OOS — the stale cloud copy kept
        // re-adding it on every pull.)
        if (key === 'fom_oos_state') {
          try {
            const oosLocalTs = _localKeyTs[key] ?? 0
            if (oosLocalTs > 0 && oosLocalTs > get().lastPush) {
              // Local has unpushed OOS edits — protect them from this pull.
              continue
            }
            const cloudStr = typeof cloudVal === 'string' ? cloudVal : JSON.stringify(cloudVal)
            // EMPTY-CLOUD GUARD: a brand-new shift row defaults to '{}'.
            // Never let an empty cloud value wipe real local OOS state —
            // OOS persists across shifts (broken gear stays broken until fixed).
            if (!cloudStr || cloudStr === '{}' || cloudStr === 'null') continue
            localStorage.setItem(key, cloudStr)
          } catch { /* silent */ }
          continue
        }
        // EMPTY-CLOUD GUARD for equipment/config arrays — same reasoning as
        // fom_oos_state above: never blank local equipment state from a fresh
        // (still-default) shift row.
        if (EQUIP_KEYS.has(key)) {
          const cv = typeof cloudVal === 'string' ? cloudVal : JSON.stringify(cloudVal ?? '')
          if (!cv || cv === '[]' || cv === '{}' || cv === 'null') continue
        }
        // Badge/radio from cloud (D1 roster table) win for new entries.
        // Local equipment values win for existing entries.
        if (key === 'fom_roster') {
          try {
            const cloudRoster = typeof cloudVal === 'string' ? JSON.parse(cloudVal) : cloudVal as Record<string, any[]>
            const localRoster = localVal ? JSON.parse(localVal) as Record<string, any[]> : {}
            const cloudTotal = Object.values(cloudRoster).flat().length
            const localTotal = Object.values(localRoster).flat().length
            // Only update if cloud has data
            if (cloudTotal > 0) {
              // Build merged roster: start with cloud, overlay local badge/radio
              const eq: Record<string, {badgeNumber: number|null, radioNumber: number|null}> = {}
              for (const entries of Object.values(localRoster)) {
                for (const e of entries as any[]) {
                  if (e.callSign) eq[e.callSign.toUpperCase()] = { badgeNumber: e.badgeNumber ?? null, radioNumber: e.radioNumber ?? null }
                }
              }
              // Also load from fom_roster_badge_radio (most authoritative)
              try {
                const eqStore = JSON.parse(localStorage.getItem('fom_roster_badge_radio') ?? '{}')
                for (const [cs, vals] of Object.entries(eqStore as Record<string, any>)) {
                  if (vals.badgeNumber != null || vals.radioNumber != null) {
                    eq[cs.toUpperCase()] = { badgeNumber: vals.badgeNumber ?? null, radioNumber: vals.radioNumber ?? null }
                  }
                }
              } catch { /* silent */ }
              // Merge: overlay local equipment onto cloud roster entries
              const merged: Record<string, any[]> = {}
              for (const [cat, entries] of Object.entries(cloudRoster)) {
                merged[cat] = (entries as any[]).map(e => {
                  const localEq = e.callSign ? eq[e.callSign.toUpperCase()] : undefined
                  return { ...e, badgeNumber: localEq?.badgeNumber ?? e.badgeNumber ?? null, radioNumber: localEq?.radioNumber ?? e.radioNumber ?? null }
                })
              }
              localStorage.setItem(key, JSON.stringify(merged))
            } else if (localTotal > 0) {
              // Cloud empty, local has data — keep local
            }
          } catch { /* silent */ }
          continue
        }

        // fom_deskOfficer: never overwrite a local non-empty selection with a cloud empty.
        // The supervisor selects the desk officer on the desktop — that choice should
        // not be wiped by a pull that brings an empty or stale value from D1.
        if (key === 'fom_deskOfficer') {
          const cloudParsed = (() => { try { return JSON.parse(cloudVal as string) } catch { return cloudVal } })()
          if (cloudParsed) localStorage.setItem(key, cloudVal as string)  // only write non-empty cloud values
          continue
        }

        // ── Last-write-wins guard ───────────────────────────────────────────────
        // If this key was written locally AFTER our last successful push, D1 does not
        // yet have our newest version. Preserve local — the next push will carry it.
        // EXCLUDED keys are always cloud-authoritative: the posting and notes must
        // always reflect the latest supervisor input from any device (e.g. upcoming
        // shift pushed from mobile must reach the desktop).
        const LWWW_EXCLUDED = new Set([
          'fom_daily_posting', 'fom_daily_posting_next', 'fom_notes',
        ])
        // Guard: never wipe a local upcoming-shift draft with an EMPTY cloud value.
        // (After the worker first gains this column, cloud returns '{}' until a
        // device pushes a real draft — blindly writing that would erase local work.)
        if (key === 'fom_daily_posting_next') {
          const cv = String(cloudVal ?? '')
          if (!cv || cv === '{}' || cv === 'null') continue
        }
        const localWriteTs = _localKeyTs[key] ?? 0
        const lastPush     = get().lastPush
        if (!LWWW_EXCLUDED.has(key) && localWriteTs > 0 && localWriteTs > lastPush) {
          // Local is newer than last push — keep it, skip cloud overwrite
          continue
        }

        localStorage.setItem(key, cloudVal as string)
        // If posting just updated from cloud, clear merge hash so mergePostingToPersonnel runs fresh
        if (key === 'fom_daily_posting') {
          try { localStorage.removeItem('fom_last_posting_merge') } catch { /* silent */ }
        }
      }

      // Write roster from fom_registered_roster — D1 is source of truth for names/ranks
      // of REGISTERED users. Locally-entered roster entries are preserved and never
      // deleted just because D1 doesn't return them (D1 roster table may be empty).
      // Strategy: start from existing local roster, MERGE D1 entries in by callSign.
      if (cloud.fom_registered_roster) {
        try {
          const d1Entries = cloud.fom_registered_roster as { name: string; callSign: string; rank: string; category: string; badge?: string; radio?: string }[]

          if (d1Entries.length > 0) {
            // Load the full existing local roster — this is the base we merge INTO
            const existingRoster: Record<string, any[]> = (() => {
              try { return JSON.parse(localStorage.getItem('fom_roster') ?? '{}') } catch { return {} }
            })()

            // Build a flat lookup of existing entries by callSign
            const localByCS: Record<string, { cat: string; idx: number; entry: any }> = {}
            for (const [cat, entries] of Object.entries(existingRoster)) {
              for (let i = 0; i < (entries as any[]).length; i++) {
                const e = (entries as any[])[i]
                if (e.callSign) localByCS[e.callSign.toUpperCase()] = { cat, idx: i, entry: e }
              }
            }

            // Start from a deep copy of the existing roster
            const merged: Record<string, any[]> = {}
            for (const [cat, entries] of Object.entries(existingRoster)) {
              merged[cat] = [...(entries as any[])]
            }

            // Merge each D1 entry: update if callSign already exists locally, add if new
            for (const d1 of d1Entries) {
              const csKey = (d1.callSign ?? '').toUpperCase()
              const local = localByCS[csKey]
              // D1 badge/radio wins if present; fall back to local values
              const badgeNumber = d1.badge ? Number(d1.badge) : (local?.entry.badgeNumber ?? null)
              const radioNumber = d1.radio ? Number(d1.radio) : (local?.entry.radioNumber ?? null)

              if (local) {
                // Update existing entry in place — preserve rank from D1
                merged[local.cat][local.idx] = {
                  ...local.entry,
                  name: d1.name || local.entry.name,
                  callSign: d1.callSign,
                  rank: d1.rank || local.entry.rank,
                  badgeNumber,
                  radioNumber,
                }
              } else {
                // New entry from D1 — add to its category with rank
                const cat = d1.category || 'Officers'
                if (!merged[cat]) merged[cat] = []
                merged[cat].push({ name: d1.name, callSign: d1.callSign, rank: d1.rank, badgeNumber, radioNumber })
              }
            }

            localStorage.setItem('fom_roster', JSON.stringify(merged))
          }
          // If D1 returns zero entries, keep existing local roster untouched
        } catch { /* silent */ }
      }

      // ── Radio layout migration v2 ─────────────────────────────────────────
      // Removes IDs 40-60 from officer radios and moves 47/50/116 to engineering.
      // Runs once per device, then stamps 'fom_radio_layout_ver' = '2' so it never
      // runs again. After migrating, marks dirty so the new layout pushes to D1
      // and permanently fixes the cloud copy too.
      const RADIO_LAYOUT_VER = '2'
      if (localStorage.getItem('fom_radio_layout_ver') !== RADIO_LAYOUT_VER) {
        try {
          const OLD_OFF_IDS = new Set(['40','41','42','43','44','45','46','47','48','49','50','51','52','53','54','55','56','57','58','59','60'])
          const offRaw = localStorage.getItem('fom_officerRadios')
          if (offRaw) {
            const offArr = JSON.parse(offRaw) as { id: string }[]
            if (offArr.some(r => OLD_OFF_IDS.has(String(r.id)))) {
              const newOff = ['102','103','104','114','106','107','115','109','110','111','112','117']
                .map(id => ({ id, available: true, assignedTo: '' }))
              localStorage.setItem('fom_officerRadios', JSON.stringify(newOff))
            }
          }
          const otherRaw = localStorage.getItem('fom_otherRadios')
          if (otherRaw) {
            const otherArr = JSON.parse(otherRaw) as { id: string; assignedTo: string }[]
            const alreadyMigrated = otherArr.some(r => String(r.id) === '47' && r.assignedTo === 'E-4')
            if (!alreadyMigrated) {
              const newOther = [
                { id: '92',  available: false, assignedTo: 'M-1'   },
                { id: '93',  available: false, assignedTo: 'O-1'   },
                { id: '98',  available: false, assignedTo: 'L-1'   },
                { id: '95',  available: false, assignedTo: 'S-01'  },
                { id: '97',  available: false, assignedTo: 'S-03'  },
                { id: '99',  available: false, assignedTo: 'S-02'  },
                { id: '105', available: false, assignedTo: 'E-2'   },
                { id: '113', available: false, assignedTo: 'E-3'   },
                { id: '47',  available: false, assignedTo: 'E-4'   },
                { id: '50',  available: false, assignedTo: 'E-5'   },
                { id: '116', available: false, assignedTo: 'E-6'   },
                { id: '108', available: false, assignedTo: 'SC-82' },
                { id: '118', available: false, assignedTo: 'SC-81' },
                { id: '10',  available: true,  assignedTo: ''      },
                { id: '28',  available: true,  assignedTo: ''      },
                { id: '51',  available: true,  assignedTo: ''      },
                { id: '52',  available: true,  assignedTo: ''      },
                { id: 'R-23',available: true,  assignedTo: ''      },
              ]
              localStorage.setItem('fom_otherRadios', JSON.stringify(newOther))
            }
          }
          localStorage.setItem('fom_radio_layout_ver', RADIO_LAYOUT_VER)
          // Push migrated layout to D1 so cloud copy is fixed permanently
          setTimeout(() => get().push(), 1000)
        } catch { /* silent */ }
      }

      // ── Radio call sign patch ─────────────────────────────────────────
      // Always ensure 95/97/99 have their permanent call sign assignments.
      // Runs on every pull until all three are confirmed correct, then stamps.
      {
        try {
          const FIXED: Record<string, string> = { '95': 'S-01', '97': 'S-03', '99': 'S-02' }
          const otherRaw = localStorage.getItem('fom_otherRadios')
          if (otherRaw) {
            const arr = JSON.parse(otherRaw) as { id: string; assignedTo: string; available: boolean }[]
            const needsPatch = arr.some(r => FIXED[String(r.id)] && r.assignedTo !== FIXED[String(r.id)])
            if (needsPatch) {
              const patched = arr.map(r =>
                FIXED[String(r.id)]
                  ? { ...r, assignedTo: FIXED[String(r.id)], available: false }
                  : r
              )
              localStorage.setItem('fom_otherRadios', JSON.stringify(patched))
              setTimeout(() => get().push(), 1000)
            }
          }
        } catch { /* silent */ }
      }

      // ── Management/engineering radio default fix ─────────────────────────
      // Historically these were seeded as available:false (always deployed) but
      // the correct default is available:true (in cabinet until explicitly deployed).
      // Run once per device, then stamp 'fom_mgmt_radio_fix' = '1'.
      if (localStorage.getItem('fom_mgmt_radio_fix') !== '1') {
        try {
          const MGMT_ENG_IDS = new Set(['92','93','98','105','113','47','50','116'])
          const orRaw = localStorage.getItem('fom_otherRadios')
          if (orRaw) {
            const arr = JSON.parse(orRaw) as { id: string; available: boolean; assignedTo: string }[]
            const fixed = arr.map(r =>
              MGMT_ENG_IDS.has(String(r.id)) && r.available === false
                ? { ...r, available: true }
                : r
            )
            if (JSON.stringify(fixed) !== orRaw) {
              localStorage.setItem('fom_otherRadios', JSON.stringify(fixed))
              setTimeout(() => get().push(), 1000)
            }
          }
          localStorage.setItem('fom_mgmt_radio_fix', '1')
        } catch { /* silent */ }
      }

      sessionStorage.setItem('fom_initial_pull_done', '1')

      set({
        status: 'ok',
        lastPull: Date.now(),
        error: '',
        pullCount: get().pullCount + 1,
      })

      // Store cloud timestamp so push guard can compare
      if (cloud._syncTs) sessionStorage.setItem('fom_cloud_sync_ts', String(cloud._syncTs))

      // Bridge: merge posting data → desktop personnel
      const postingMerged = mergePostingToPersonnel()

      // Dispatch event so stores re-hydrate immediately — no component lifecycle dependency
      window.dispatchEvent(new CustomEvent('castellan:pull-complete'))

      // If posting merge changed personnel, mark dirty so it pushes back
      if (postingMerged) {
        set({ dirtyAt: Date.now() })
        setTimeout(() => get().push(), 1000)
      }

    } catch (e) {
      const msg = String(e)
      // V1 helpers throw on non-2xx — 401 means session is invalid, sign user out
      if (msg.includes('401')) {
        clearSession(); window.location.reload(); return
      }
      set({ status: 'error', error: msg })
    }

    _pulling = false
  },

  async push() {
    if (_pushing) return
    if (!sessionStorage.getItem('fom_initial_pull_done')) return
    if (!hasRealData()) return

    // Refuse to push if we've never successfully pulled from cloud this session
    // — prevents stale offline data from overwriting fresh cloud state
    const cloudSyncTs = sessionStorage.getItem('fom_cloud_sync_ts')
    if (!cloudSyncTs) return

    // NOTE: syncPersonnelToPosting() is intentionally NOT called here.
    // Calling it before buildPayload() would overwrite fom_daily_posting with
    // stale personnel data, wiping any posting edits the supervisor just made.
    // Personnel → Posting sync is handled by the desktop store subscriber
    // when personnel actually changes, not on every push.

    const payload = buildPayload()
    const dataOnly: Record<string, unknown> = {}
    for (const k of Object.keys(payload)) {
      if (k !== '_ts') dataOnly[k] = payload[k]
    }
    const hash = JSON.stringify(dataOnly)
    if (hash === _lastHash) return

    _pushing = true
    set({ status: 'pushing' })

    try {
      const data = await workerPostV1<{ ok?: boolean; stale?: boolean; _syncTs?: string; error?: string }>(
        '/api/v1/sync/push',
        dataOnly,
      )
      if (data.stale) {
        // Our local data is older than what's on the server.
        // Do a force pull instead of pushing stale data.
        console.warn('[sync] push rejected as stale — pulling fresh data from server')
        _pushing = false
        set({ dirtyAt: 0 })
        await get().pull()
        return
      }
      if (data.ok) {
        _lastHash = hash
        // Clear per-key dirty timestamps — our changes are now in D1.
        // Future pulls can freely overwrite with cloud data.
        for (const k of Object.keys(_localKeyTs)) delete _localKeyTs[k]
        try { localStorage.removeItem('_castellan_dirty_ts') } catch { /* silent */ }
        set({ status: 'ok', lastPush: Date.now(), error: '' })
      } else {
        set({ status: 'error', error: data.error ?? 'Push rejected' })
      }
    } catch (e) {
      const msg = String(e)
      if (msg.includes('401')) {
        clearSession(); window.location.reload(); return
      }
      set({ status: 'error', error: msg })
    }

    _pushing = false
  },

  async forcePull() {
    // Reset dirtyAt guard so pull is not blocked after recent edits
    set({ dirtyAt: 0 })
    await get().pull()
  },

  async forcePush() {
    // Reset hash so push is not blocked by "no change" guard
    _lastHash = ''
    // Also ensure dirtyAt doesn't block the follow-up pull
    await get().push()
  },
}))

export function syncColor(status: SyncStatus): string {
  switch (status) {
    case 'ok':      return '#22c55e'
    case 'pulling':
    case 'pushing': return '#f59e0b'
    case 'error':   return '#ef4444'
    default:        return '#64748b'
  }
}

let _intervalStarted = false

export function startSyncIntervals(): () => void {
  if (_intervalStarted) return () => {}
  _intervalStarted = true
  useSyncStore.getState().pull()
  const pullId = setInterval(() => useSyncStore.getState().pull(), 30_000)  // pull every 30s
  const pushId = setInterval(() => useSyncStore.getState().push(), 300_000) // push heartbeat every 5min
  return () => { clearInterval(pullId); clearInterval(pushId); _intervalStarted = false }
}

export function markDirty() { useSyncStore.getState().markDirty() }
